import populateRentalCarBusStopData from './populateRentalCarBusStopData';
import convertDateTimeToString from './convertDateTimeToString';
import createTxpDataRow from './createTxpDataRow';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import Car from '../../schema/innerSchema/Car';
import CardItem from '../../schema/viewSchema/CardItem';
import CardRole from '../../schema/innerSchema/CardRole';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import Place from '../../schema/innerSchema/Place';
import RentalCarReservation from '../../schema/RentalCarReservation';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpDataRow from '../../schema/viewSchema/TxpDataRow';
import TxpStrings from '../../schema/configSchema/TxpStrings';

/**
 *  Extracts the rental car specific data needed for displaying the card
 */
export default function createRentalCarCard(
    rentalCarEntity: RentalCarReservation,
    isBusStop: boolean,
    activityType: ActivityType,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    cardRole?: CardRole,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons
): CardItem {
    let summaryFirstLine: string;
    let busStopData: BusStopData;
    let rentalCarText: string;
    let dropOffTime: string;
    let pickUpTime: string;
    let detailsDropOffLine: TxpDataRow;
    let companyText: string;
    let locationText: string;
    let rentalCompanyPlace: Place;
    let rentalCarDetails: Car = rentalCarEntity.car;
    let rentalCompanyName: string = rentalCarEntity.provider ? rentalCarEntity.provider.name : null;
    let parsedPickUpTime = convertDateTimeToString(
        rentalCarEntity.pickupTime,
        false /*isAccessible*/,
        txpStrings.formatters
    );
    let summarySecondLine: string =
        rentalCarEntity.reservationId &&
        txpStrings.formatters.format(
            txpStrings.strings.reservationCodeText,
            rentalCarEntity.reservationId
        );
    let parsedDropOffTime = convertDateTimeToString(
        rentalCarEntity.dropoffTime,
        false /*isAccessible*/,
        txpStrings.formatters
    );

    const actionButtonData = rentalCarEntity.modifyReservationUrl
        ? {
              actionIconName: detailsIcons.edit,
              actionUrl: rentalCarEntity.modifyReservationUrl,
              actionButtonText: txpStrings.strings.modifyReservationText,
              actionName: ActionNameConstants.ModifyReservation,
          }
        : null;

    if (
        rentalCarDetails &&
        rentalCarDetails.model &&
        rentalCarDetails.brand &&
        rentalCarDetails.brand.name
    ) {
        rentalCarText = txpStrings.formatters.format(
            txpStrings.strings.carText,
            rentalCarDetails.brand.name,
            rentalCarDetails.model
        );
    }

    if (cardRole == CardRole.Combined) {
        rentalCompanyPlace = rentalCarEntity.dropoffLocation;
        summaryFirstLine = txpStrings.formatters.format(
            txpStrings.strings.reservationText,
            rentalCompanyName
        );
        dropOffTime = parsedDropOffTime;
        pickUpTime = parsedPickUpTime;
        detailsDropOffLine = createTxpDataRow(dropOffTime, txpStrings.strings.dropOffText);
    } else if (activityType == ActivityType.RentalCarPickup) {
        rentalCompanyPlace = rentalCarEntity.pickupLocation;
        summaryFirstLine = txpStrings.formatters.format(
            txpStrings.strings.pickUpSuffixText,
            rentalCompanyName
        );
        pickUpTime = parsedPickUpTime;
    } else if (activityType == ActivityType.RentalCarDropOff) {
        rentalCompanyPlace = rentalCarEntity.dropoffLocation;
        summaryFirstLine = txpStrings.formatters.format(
            txpStrings.strings.dropOffSuffixText,
            rentalCompanyName
        );
        dropOffTime = parsedDropOffTime;
        detailsDropOffLine = createTxpDataRow(
            dropOffTime,
            txpStrings.strings.dropOffText,
            detailsIcons.clock
        );
    } else {
        return null;
    }

    if (rentalCompanyPlace) {
        companyText = rentalCompanyPlace.name;
        locationText = rentalCompanyPlace.address ? rentalCompanyPlace.address.streetAddress : null;
    }

    if (isBusStop) {
        busStopData = populateRentalCarBusStopData(
            rentalCarEntity,
            activityType,
            rentalCompanyPlace,
            txpStrings,
            detailsIcons.car
        );
    }
    locationText = showLocation ? locationText : null;

    return {
        id: activityId,
        summaryData: {
            firstLine: { text: summaryFirstLine },
            secondLine: { text: summarySecondLine },
        },
        entityImage: entityImage,
        entityIcon: entityIcon,
        activityType: activityType,
        entityType: EntityType.RentalCarReservation,
        busStopData: busStopData,
        detailsData: {
            // TODO: VSO# 30914: Extract the creation of details data to a separate util and
            // see if we can return a list of TxpDataRows, avoiding the xxxLine labels
            firstLine: createTxpDataRow(companyText, null, detailsIcons.building),
            secondLine: createTxpDataRow(rentalCarText, null, detailsIcons.parkingLocation),
            thirdLine: createTxpDataRow(
                pickUpTime,
                txpStrings.strings.pickUpText,
                detailsIcons.clock
            ),
            fourthLine: detailsDropOffLine,
            fifthLine: createTxpDataRow(
                locationText,
                null,
                detailsIcons.location,
                false /* is required */,
                null /* placeholder */,
                true /* includeToolTip */
            ),
        },
        brandsSummaryFirstLine: summaryFirstLine,
        brandsSummarySecondLine: txpStrings.formatters.formatWeekDayDateTime(
            rentalCarEntity.pickupTime
        ),
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
    } as CardItem;
}
